import React, { useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { GetPaymentDocument, type GetPaymentQuery } from '@/generated/root-types';
import {
  paymentEventTypeColors,
  paymentStateColors,
  translatePaymentDirection,
  translatePaymentEventType,
  translatePaymentState,
} from '@/utils/billingStateLabels';
import {
  detailBackButtonSx,
  detailHeaderRowWrapSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

type PaymentHistoryRow = NonNullable<
  NonNullable<GetPaymentQuery['payment']>['paymentHistories']
>[number];

function formatAdditionalPropertiesJson(s: string | null | undefined): string | null {
  if (s == null || s === '') return null;
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

type PaymentHistoryTimelineItemProps = {
  h: PaymentHistoryRow;
  isLast: boolean;
  formatDateTime: (d?: string | null) => string;
};

function StateTransitionChips({ h, t }: { h: PaymentHistoryRow; t: TFunction }) {
  const { fromState, toState } = h;
  if (fromState == null && toState == null) {
    return null;
  }
  if (fromState != null && toState != null) {
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        useFlexGap
        columnGap={1}
        rowGap={0.5}
      >
        <Chip
          label={translatePaymentState(t, fromState)}
          size="small"
          variant="outlined"
          color={paymentStateColors[fromState] || 'default'}
        />
        <ArrowForward
          fontSize="small"
          color="action"
          sx={{ opacity: 0.75, flexShrink: 0 }}
          aria-hidden
        />
        <Chip
          label={translatePaymentState(t, toState)}
          size="small"
          color={paymentStateColors[toState] || 'default'}
        />
      </Stack>
    );
  }
  if (fromState != null) {
    return (
      <Chip
        label={translatePaymentState(t, fromState)}
        size="small"
        variant="outlined"
        color={paymentStateColors[fromState] || 'default'}
        sx={{ maxWidth: '100px' }}
      />
    );
  }
  if (toState != null) {
    return (
      <Chip
        label={translatePaymentState(t, toState)}
        size="small"
        color={paymentStateColors[toState] || 'default'}
        sx={{ maxWidth: '100px' }}
      />
    );
  }
  return null;
}

function PaymentHistoryTimelineItem({
  h,
  isLast,
  formatDateTime,
}: PaymentHistoryTimelineItemProps) {
  const { t } = useTranslation();
  const additional = formatAdditionalPropertiesJson(h.additionalPropertiesJson);
  const eventChip = paymentEventTypeColors[h.eventType] ?? 'default';

  return (
    <TimelineItem
      sx={{
        minHeight: 0,
        pb: isLast ? 0 : 0.5,
      }}
    >
      <TimelineOppositeContent
        sx={{
          m: 0,
          p: 0,
          maxHeight: 0,
          minWidth: 0,
          maxWidth: 0,
          flex: 0,
          overflow: 'hidden',
          opacity: 0,
          visibility: 'hidden',
        }}
      />
      <TimelineSeparator>
        <TimelineDot color={isLast ? 'primary' : 'grey'} variant={isLast ? 'filled' : 'outlined'} />
        {!isLast && <TimelineConnector sx={{ bgcolor: 'divider' }} />}
      </TimelineSeparator>
      <TimelineContent
        sx={{
          py: 0.5,
          pl: 2,
          pr: 0,
          m: 0,
          minWidth: 0,
        }}
      >
        <Stack spacing={1.25}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            columnGap={1}
            rowGap={0.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Chip
              size="small"
              variant="outlined"
              color={eventChip}
              label={translatePaymentEventType(t, h.eventType)}
              sx={{ fontWeight: 600, maxWidth: '100%' }}
            />
            <Typography
              component="time"
              dateTime={h.createdAt}
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {formatDateTime(h.createdAt)}
            </Typography>
          </Stack>

          <StateTransitionChips h={h} t={t} />

          {h.reason != null && h.reason !== '' ? (
            <Typography variant="body2" color="text.primary">
              {t('payments.historyReasonLine', { value: h.reason })}
            </Typography>
          ) : null}

          {h.errorMessage != null && h.errorMessage !== '' ? (
            <Typography
              variant="body2"
              color="error"
              sx={{ wordBreak: 'break-word' }}
              component="p"
              m={0}
            >
              {t('payments.historyErrorLine', { value: h.errorMessage })}
            </Typography>
          ) : null}

          {h.providerEventId != null && h.providerEventId !== '' ? (
            <Typography
              variant="caption"
              color="text.secondary"
              fontFamily="ui-monospace, monospace"
              fontSize="0.75rem"
              sx={{ wordBreak: 'break-all' }}
              component="p"
              m={0}
            >
              {t('payments.historyProviderLine', { id: h.providerEventId })}
            </Typography>
          ) : null}

          {additional != null ? (
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                component="span"
              >
                {t('payments.historyAdditional')}
              </Typography>
              <Box
                component="pre"
                sx={() => ({
                  m: 0,
                  p: 1.25,
                  maxHeight: 120,
                  overflow: 'auto',
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  lineHeight: 1.5,
                  fontFamily: 'ui-monospace, monospace',
                  color: 'text.primary',
                  border: 1,
                  borderStyle: 'solid',
                  borderColor: 'divider',
                  backgroundColor: 'action.hover',
                })}
              >
                {additional}
              </Box>
            </Stack>
          ) : null}
        </Stack>
      </TimelineContent>
    </TimelineItem>
  );
}

export default function PaymentDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTenantId = (location.state as { fromTenantId?: string })?.fromTenantId;
  const fromAccountingDocumentId = (location.state as { fromAccountingDocumentId?: string })
    ?.fromAccountingDocumentId;

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    amount != null
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
          amount,
        )
      : '–';
  const formatDateTime = useCallback(
    (date?: string | null) =>
      date
        ? new Date(date).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })
        : '–',
    [locale],
  );

  const { data, loading, error } = useQuery<GetPaymentQuery>(GetPaymentDocument, {
    variables: { id },
    skip: !id,
  });

  const payment = data?.payment;
  const doc = payment?.accountingDocument;
  const organizationId = doc?.subscription?.organizationUnit?.organization?.id;
  const customerName =
    doc?.subscription?.organizationUnit?.organization?.name ?? doc?.tenant?.name ?? null;
  const navigateToCustomerId = fromTenantId ?? organizationId ?? undefined;

  const historyRows: PaymentHistoryRow[] = useMemo(() => {
    if (!payment?.paymentHistories?.length) return [];
    return [...payment.paymentHistories].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [payment]);

  const handleBack = () => {
    if (fromTenantId) navigate(`/customers/${fromTenantId}`);
    else if (fromAccountingDocumentId) navigate(`/invoices/${fromAccountingDocumentId}`);
    else navigate('/payments');
  };

  if (loading) {
    return (
      <Box
        sx={{
          ...detailPageContainerSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>
        <Typography color="error">{error?.message ?? t('errors.paymentNotFound')}</Typography>
      </Box>
    );
  }

  const titleRef = payment.reference ?? doc?.number ?? '–';

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={{ ...detailHeaderRowWrapSx, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={detailBackButtonSx}>
          {t('common.back')}
        </Button>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="h4" component="h1" sx={detailTitleSx}>
            {t('payments.paymentLabel')} · {titleRef}
          </Typography>
        </Box>
        <Chip
          label={translatePaymentState(t, payment.state)}
          color={paymentStateColors[payment.state] || 'default'}
          size="small"
        />
        {navigateToCustomerId && (
          <Button size="small" onClick={() => navigate(`/customers/${navigateToCustomerId}`)}>
            {t('common.toCustomer')}
          </Button>
        )}
        {doc?.id && (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              navigate(`/invoices/${doc.id}`, { state: { fromTenantId: navigateToCustomerId } })
            }
          >
            {t('payments.openInvoice')}
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        elevation={0}
        sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('payments.timelineTitle')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: historyRows.length > 0 ? 2.5 : 0 }}
        >
          {t('payments.historyDescription')}
        </Typography>
        {historyRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {t('payments.historyEmpty')}
          </Typography>
        ) : (
          <Timeline
            position="right"
            sx={{
              p: 0,
              m: 0,
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {historyRows.map((h, index) => (
              <PaymentHistoryTimelineItem
                key={h.id}
                h={h}
                isLast={index === historyRows.length - 1}
                formatDateTime={formatDateTime}
              />
            ))}
          </Timeline>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('organizationDetail.masterData')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('customers.customerOrg')}
            </Typography>
            <Typography variant="body2">{customerName ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.direction')}
            </Typography>
            <Typography variant="body2">
              {translatePaymentDirection(t, payment.direction)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.reference')}
            </Typography>
            <Typography variant="body2">{payment.reference ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.invoice')}
            </Typography>
            <Typography variant="body2">{doc?.number ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.gateway')}
            </Typography>
            <Typography variant="body2">{payment.paymentGateway?.name ?? '–'}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('invoices.amounts')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.net')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(payment.amountNet, payment.currencyCode)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.gross')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(payment.amountGross, payment.currencyCode)}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
